import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { CardData } from './builder'

interface Option {
    id: string
    text: string
}

interface QuestionConfig {
    options?: Option[]
    scale?: number
    maxLabel?: string
    minLabel?: string
    displayType?: string
    emojiImages?: string[] // Array of emoji image paths
    placeholder?: string
    randomize?: boolean
    allowOther?: boolean
    otherLabel?: string
    maxSelect?: number | null
    minSelect?: number | null
    nextQuestionId?: string | null
    isTerminal?: boolean
}

export interface Question {
    questionId: string
    surveyId: string
    text: string
    description: string | null
    type:
        | 'single_select'
        | 'multi_select'
        | 'nps'
        | 'rating'
        | 'text'
        | 'date'
        | 'phone'
        | 'drop_down'
    isRequired: boolean
    orderIndex: number
    config: QuestionConfig
    logic: Record<string, any>
}

export interface PreviewState {
    surveyId: string | null
    name: string
    description: string
    thankYouCard: CardData | null
    questions: Question[] | []
    selectedQuestionId: string | null
    currentQuestionIndex: number
    previewResponses: Record<
        string,
        {
            optionId?: string
            optionIds?: string[]
            otherValue?: string
            value?: any
        }
    >
    questionPath: Record<string, string | null>
}

const initialState: PreviewState = {
    surveyId: null,
    name: 'Untitled Survey',
    description: '',
    questions: [],
    selectedQuestionId: null,
    currentQuestionIndex: 0,
    previewResponses: {},
    questionPath: {},
    thankYouCard: null,
}

const previewSlice = createSlice({
    name: 'preview',
    initialState,
    reducers: {
        resetPreview: (state) => {
            state.surveyId = null
            state.name = 'Untitled Survey'
            state.description = ''
            state.questions = []
            state.selectedQuestionId = null
            state.currentQuestionIndex = 0
            state.previewResponses = {}
            state.questionPath = {}
            state.thankYouCard = null
        },

        updatePreviewDetails: (
            state,
            action: PayloadAction<{
                questions: Question[]
                name: string
                thankYouCard: CardData | null
            }>
        ) => {
            state.questions = action.payload.questions
            state.name = action.payload.name
            state.thankYouCard = action.payload.thankYouCard
            if (action.payload.questions.length > 0) {
                state.selectedQuestionId =
                    action.payload.questions[0].questionId
                state.questionPath = {
                    [action.payload.questions[0].questionId]: null,
                }
            } else {
                state.selectedQuestionId = null
            }
        },

        selectPreviewQuestion: (state, action: PayloadAction<string>) => {
            const index = state.questions.findIndex(
                (q) => q.questionId === action.payload
            )
            if (index !== -1) {
                state.selectedQuestionId = action.payload
                state.currentQuestionIndex = index
            }
        },

        addPreviewQuestionPath: (
            state,
            action: PayloadAction<{
                questionId: string
                fromQuestionId: string | null
            }>
        ) => {
            state.questionPath[action.payload.questionId] =
                action.payload.fromQuestionId
        },

        updatePreviewResponse: (
            state,
            action: PayloadAction<{
                questionId: string
                response: {
                    optionId?: string
                    optionIds?: string[]
                    otherValue?: string
                    value?: any
                }
            }>
        ) => {
            const { questionId, response } = action.payload
            state.previewResponses[questionId] = response
        },

        clearPreviewResponses: (state) => {
            state.previewResponses = {}
        },

        resetQuestionPath: (state) => {
            state.questionPath = {}
        },
    },
})

export const {
    resetPreview,
    updatePreviewDetails,
    selectPreviewQuestion,
    addPreviewQuestionPath,
    updatePreviewResponse,
    clearPreviewResponses,
    resetQuestionPath,
} = previewSlice.actions

export default previewSlice.reducer
